import React, { useEffect, useMemo } from "react";
import {
  CreditCard,
  Wallet,
  Banknote,
  QrCode,
  ChevronDown,
  Landmark,
} from "lucide-react";

import MemberPicker from "./MemberPicker";

const METHODS = [
  { value: "cash", label: "Cash", icon: Banknote },
  { value: "card", label: "Card", icon: CreditCard },
  { value: "ewallet", label: "E-Wallet", icon: Wallet },
  { value: "transfer", label: "Bank Transfer", icon: Landmark },
  { value: "QRIS", label: "QRIS", icon: QrCode },
];

export default function Payment({
  subtotal,                // hanya untuk preview diskon
  total,                   // 🔥 TOTAL FINAL dari SaleSubmitter
  globalDiscounts = [],
  onPayment,
  onCancel,
  loading,
  onSummaryChange,
  registerOpen = true,
  checkout,
  onCheckoutChange,
  storeLocationId,
}) {
  const method = checkout?.payment_method ?? "cash";
  const customer = checkout?.customer_name ?? "General";
  const buyerName = checkout?.buyer_name ?? "";
  const paid = checkout?.paid ?? "";
  const reference = checkout?.reference ?? "";
  const note = checkout?.note ?? "";
  const globalDiscountId = checkout?.global_discount_id ?? null;
  const member = checkout?.member ?? null;
  // Name comes from the attached member (or will, once they tap Pasang Member).
  const nameLocked = Boolean(member) || customer === "Member";

  const patchCheckout = (patch) => {
    onCheckoutChange?.({
      payment_method: method,
      customer_name: customer,
      buyer_name: buyerName,
      paid,
      reference,
      note,
      global_discount_id: globalDiscountId,
      member,
      ...patch,
    });
  };

  const selectedDiscount = useMemo(
    () => globalDiscounts.find((d) => d.id === globalDiscountId),
    [globalDiscounts, globalDiscountId]
  );

  /* ===== DISCOUNT PREVIEW (FE ONLY) ===== */
  const discountPreview = useMemo(() => {
    if (!selectedDiscount) return 0;

    let amount =
      selectedDiscount.kind === "PERCENT"
        ? (Number(subtotal || 0) * selectedDiscount.value) / 100
        : Number(selectedDiscount.value || 0);

    if (selectedDiscount.max_amount != null) {
      amount = Math.min(amount, Number(selectedDiscount.max_amount));
    }

    return Math.max(0, Math.round(amount));
  }, [selectedDiscount, subtotal]);

  /* ===== FINAL TOTAL (SOURCE OF TRUTH) ===== */
  const finalTotal = useMemo(
    () => (typeof total === "number" ? total : 0),
    [total]
  );

  /* ===== KIRIM DISCOUNT KE SaleSubmitter ===== */
  useEffect(() => {
    onSummaryChange?.({
      discountAmount: discountPreview,
    });
  }, [discountPreview, onSummaryChange]);

  /* ===== AUTO FILL NON-CASH ===== */
  useEffect(() => {
    if (method === "cash" || !onCheckoutChange) return;
    onCheckoutChange({
      payment_method: method,
      customer_name: customer,
      buyer_name: buyerName,
      paid: String(finalTotal || 0),
      reference,
      note,
      global_discount_id: globalDiscountId,
      member,
    });
  }, [
    method,
    finalTotal,
    customer,
    buyerName,
    reference,
    note,
    globalDiscountId,
    member,
    onCheckoutChange,
  ]);

  const change = useMemo(() => {
    if (method !== "cash") return 0;
    return Math.max(0, Number(paid || 0) - finalTotal);
  }, [paid, finalTotal, method]);

  const canSubmit = useMemo(() => {
    if (loading) return false;
    if (!registerOpen) return false;
    if (finalTotal <= 0) return false;
    if (method === "cash") return Number(paid) >= finalTotal;
    return true;
  }, [loading, method, paid, finalTotal]);

  const submit = () => {
    onPayment?.({
      payment_method: method,
      paid_amount:
        method === "cash" ? Number(paid || 0) : finalTotal,
      reference: reference?.trim() || null,
      customer_name: customer || null,
      buyer_name: buyerName?.trim() || null,
      member_id: member?.id ?? null,
      note,
      global_discount_id: globalDiscountId,
      total_preview: finalTotal, // FE preview
    });
  };

  const CurrentIcon =
    METHODS.find((m) => m.value === method)?.icon || Banknote;

  return (
    <div className="mt-4 border-t pt-4">
      {/* ===== DISCOUNT ===== */}
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Discount Transaction
      </label>

      <div className="relative">
        <select
          value={globalDiscountId || ""}
          onChange={(e) =>
            patchCheckout({
              global_discount_id: e.target.value ? Number(e.target.value) : null,
            })
          }
          className="w-full h-11 appearance-none rounded-full border px-4 pr-9
                     text-sm bg-white border-gray-300
                     focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">No Discount</option>
          {globalDiscounts.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      </div>

      {selectedDiscount && (
        <div className="mt-1 text-xs text-blue-600">
          Promo applied (preview): −Rp
          {discountPreview.toLocaleString("id-ID")}
        </div>
      )}

      {/* ===== CUSTOMER ===== */}
      <div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Customer Type (optional)
        </label>
        <div className="relative">
          <select
            value={customer}
            onChange={(e) => {
              const next = e.target.value;
              patchCheckout({
                customer_name: next,
                // Member attach is only for type Member. Switching away
                // drops the attached member so the next Pay cannot mix them.
                ...(next !== "Member" ? { member: null } : {}),
              });
            }}
            className="w-full h-11 appearance-none rounded-full border px-4 pr-9
                       text-sm bg-white border-gray-300
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="General">General</option>
            <option value="Retail">Retail</option>
            <option value="Corporate">Corporate</option>
            <option value="Influencer">Influencer</option>
            <option value="Member">Member</option>
            <option value="Marketplace">Marketplace</option>
            <option value="Other">Other</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>
      </div>

      <div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Customer name (optional)
        </label>
        <input
          type="text"
          value={buyerName}
          onChange={(e) => {
            if (nameLocked) return;
            patchCheckout({ buyer_name: e.target.value });
          }}
          readOnly={nameLocked}
          disabled={nameLocked}
          className={`w-full h-11 rounded-full border px-4 text-sm
                     border-gray-300 focus:outline-none
                     focus:ring-2 focus:ring-blue-500
                     ${nameLocked ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"}`}
          placeholder={nameLocked ? "Pasang member untuk mengisi nama" : "Nama pelanggan…"}
          maxLength={100}
        />
      </div>

      {/* ===== MEMBER (customer database) ===== */}
      <MemberPicker
        storeLocationId={storeLocationId}
        value={member}
        total={finalTotal}
        disabled={customer !== "Member"}
        onChange={(m) =>
          patchCheckout({
            member: m,
            // Keep the free-text type in sync so History/receipt still reads
            // sensibly for a member sale.
            customer_name: m ? "Member" : customer,
            // Prefill from the member. The name field is locked while a
            // member is attached so it cannot be edited by accident.
            ...(m ? { buyer_name: m.name || "" } : {}),
          })
        }
      />

      {/* ===== PAYMENT METHOD ===== */}
      <div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Payment Method
        </label>
        <div className="relative">
          <select
            value={method}
            onChange={(e) => {
              const newMethod = e.target.value;
              patchCheckout({
                payment_method: newMethod,
                paid:
                  newMethod === "cash"
                    ? paid
                    : String(finalTotal || 0),
              });
            }}
            className="w-full h-11 appearance-none rounded-full border px-4 pr-9
                       text-sm bg-white border-gray-300
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>

        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
          <CurrentIcon className="h-4 w-4" />
          <span>{METHODS.find((m) => m.value === method)?.label}</span>
        </div>
      </div>

      {/* ===== RECEIVED ===== */}
      <div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Received (IDR)
        </label>
        <input
          type="number"
          inputMode="numeric"
          value={method === "cash" ? paid : String(finalTotal || 0)}
          onChange={(e) => {
            if (method === "cash") patchCheckout({ paid: e.target.value });
          }}
          readOnly={method !== "cash"}
          disabled={method !== "cash"}
          className={`w-full h-11 rounded-full border px-4 text-sm
                     border-gray-300 focus:outline-none
                     focus:ring-2 focus:ring-blue-500
                     ${method !== "cash" ? "bg-gray-100 cursor-not-allowed" : ""}`}
          placeholder="Enter amount"
        />

        {method === "cash" && (
          <div className="flex items-center justify-between mt-1 text-sm">
            <span className="text-gray-500">Change</span>
            <span className="font-semibold">
              Rp{change.toLocaleString("id-ID")}
            </span>
          </div>
        )}

        {method === "cash" && paid && Number(paid) < finalTotal && (
          <div className="text-red-600 text-xs mt-1">
            Cash received is less than total.
          </div>
        )}
      </div>

      {/* ===== NOTE ===== */}
      <div className="mt-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Note (optional)
        </label>
        <textarea
          rows={2}
          value={note}
          onChange={(e) => patchCheckout({ note: e.target.value })}
          className="w-full border rounded-2xl px-4 py-2 text-sm
                     border-gray-300 focus:outline-none
                     focus:ring-2 focus:ring-blue-500"
          placeholder="Transaction note…"
        />
      </div>

      {/* ===== ACTIONS ===== */}
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 h-11 border border-gray-300 rounded-full
                     text-sm hover:bg-gray-50"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          className="flex-1 h-11 bg-blue-600 text-white rounded-full
                     text-sm font-semibold hover:bg-blue-700
                     disabled:opacity-60"
          disabled={!canSubmit}
        >
          {loading ? "Processing…" : "Pay"}
        </button>
      </div>

      {!registerOpen && (
        <div className="mt-2 text-xs text-red-600">
          Register belum dibuka. Silakan buka register terlebih dahulu sebelum melakukan pembayaran.
        </div>
      )}
    </div>
  );
}
